/**
 * API COVERAGE — DestinationsController (/api/destinations): Module 1 Places · Destinations (D1–D6), plus the
 * shared map search, Fetch with AI and "+ Add new" lists used by every places master.
 */
import { api, http } from '@/lib/apiClient';
import type { Translation } from './pois.api';

export type PlaceStatus = 'Draft' | 'Active' | 'Inactive' | 'PendingApproval';
export type PlaceLevel = 'CityTown' | 'Village' | 'Area';
export type LookupKind = 'DestinationType' | 'AttractionType' | 'ActivityType' | 'SubTourType' | 'VehicleType';

export const LEVEL_LABEL: Record<PlaceLevel, string> = { CityTown: 'City/Town', Village: 'Village', Area: 'Area' };
export const STATUS_LABEL: Record<PlaceStatus, string> = { Draft: 'Draft', Active: 'Active', Inactive: 'Inactive', PendingApproval: 'Pending approval' };
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface NamedRef { id: number; code: string; name: string }

export interface DestinationListItem {
  id: number; code: string; name: string; stateName?: string | null; countryIso2: string; countryName?: string | null;
  level: PlaceLevel; parentName?: string | null; types: string[]; attractions: number; hotels: number; status: PlaceStatus;
  hasCover: boolean; alsoKnownAs: string[];
}
export interface DestinationList { items: DestinationListItem[]; total: number; all: number; draftsWithoutCover: number }

export interface TransferPoint { kind: 'Airport' | 'Railway' | 'Bus'; name: string; code?: string | null; distanceKm?: number | null; driveMinutes?: number | null }

export interface Destination {
  id: number; code: string; name: string; alsoKnownAs: string[]; level: PlaceLevel; parentId?: number | null; parentName?: string | null;
  countryIso2: string; countryName?: string | null; stateCode?: string | null; stateName?: string | null;
  latitude?: number | null; longitude?: number | null; elevationFt?: number | null; timeZone?: string | null;
  types: string[]; bestMonths: number[]; bestMonthsText: string; weatherNotes?: string | null;
  permitRequired: boolean; permitDetails?: string | null; transferPoints: TransferPoint[];
  shortDescription?: string | null; longDescription?: string | null; coverFileId?: number | null; galleryFileIds: number[];
  tags: string[]; status: PlaceStatus; fromLibrary: boolean; createdAt: string; updatedAt?: string | null; translations?: Translation[];
  attractions: number; activities: number; subTours: number; hotels: number; routes: number;
}

export interface SaveDestination {
  name: string; alsoKnownAs: string[]; level: PlaceLevel; parentId?: number | null; countryIso2: string; stateCode?: string | null;
  latitude?: number | null; longitude?: number | null; elevationFt?: number | null; timeZone?: string | null; types: string[];
  bestMonths: number[]; weatherNotes?: string | null; permitRequired: boolean; permitDetails?: string | null;
  transferPoints: TransferPoint[]; shortDescription?: string | null; longDescription?: string | null; coverFileId?: number | null;
  galleryFileIds: number[]; tags: string[]; translations?: Translation[]; activate: boolean; confirmNearDuplicate?: boolean;
}

export interface RelatedPoi { id: number; code: string; name: string; kind: string; type?: string | null; status: PlaceStatus }
export interface DestinationRelated {
  attractions: RelatedPoi[]; activities: RelatedPoi[];
  subTours: { id: number; code: string; name: string; type: string; status: PlaceStatus }[];
  routes: { id: number; code: string; from: string; to: string; distanceKm: number; summerMinutes: number; winterMinutes?: number | null }[];
  usedIn: string[];
}

export interface Skipped { id: number; name: string; reason: string }
export interface BulkResult { done: number; doneNames: string[]; skipped: Skipped[]; message: string }
export interface MergeSide { id: number; code: string; name: string; status: PlaceStatus; attractions: number; activities: number; hotels: number; routes: number; subTours: number; tours: number }

export interface ImportRow { row: number; name?: string | null; country?: string | null; state?: string | null; level?: string | null; error?: string | null }
export interface ImportCheck { fileName: string; rows: ImportRow[]; ok: number; errors: number; message: string }

export interface GeoOptions { countries: { iso2: string; name: string }[]; states: { code: string; name: string }[] }
/** Settings › Where we operate: Indian states, and other countries as a whole. In use = holds destinations, cannot be taken off. */
export interface OperatingRegions { stateCodes: string[]; countryCodes: string[]; statesInUse: string[]; countriesInUse: string[] }
export interface MapPlace { name: string; display: string; latitude: number; longitude: number; countryIso2?: string | null; countryName?: string | null; stateName?: string | null; city?: string | null }
export interface PlaceLookup { id: number; kind: LookupKind; name: string; description?: string | null; seats?: number | null; largeBags?: number | null }

export interface AiTransfer { name?: string | null; code?: string | null; distanceKm?: number | null; driveMinutes?: number | null }
export interface AiPlaceFetch {
  countryIso2?: string | null; stateName?: string | null; elevationFt?: number | null; timeZone?: string | null; alsoKnownAs?: string[] | null;
  types?: string[] | null; bestMonths?: number[] | null; weatherNotes?: string | null; permitRequired?: boolean | null; permitDetails?: string | null;
  nearestAirport?: AiTransfer | null; nearestRailway?: AiTransfer | null; nearestBus?: AiTransfer | null;
  shortDescription?: string | null; longDescription?: string | null; address?: string | null; poiType?: string | null; managedBy?: string | null;
  vehicleAccess?: string | null; accessNote?: string | null; visitMinutes?: number | null; bestTimeOfDay?: string | null; dressCode?: string | null;
  photography?: string | null; wheelchair?: string | null; tips?: string | null; dayText?: string | null; durationMinutes?: number | null;
  seasonMonths?: number[] | null; minAge?: number | null; maxAge?: number | null; fitnessLevel?: string | null;
}

export interface ListParams {
  search?: string; country?: string; state?: string; type?: string; status?: string; level?: string; missingCover?: boolean; page?: number; pageSize?: number;
}

const base = '/destinations';

async function download(url: string, body: unknown | undefined, fallbackName: string) {
  const res = body === undefined
    ? await http.get(url, { responseType: 'blob' })
    : await http.post(url, body, { responseType: 'blob' });
  const name = /filename="?([^";]+)"?/i.exec(res.headers['content-disposition'] ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
}

export const destinationsApi = {
  list: (p: ListParams) => api.get<DestinationList>(base, p as Record<string, unknown>),
  options: (search?: string, exceptId?: number) => api.get<NamedRef[]>(`${base}/options`, { search, exceptId }),
  get: (id: number) => api.get<Destination>(`${base}/${id}`),
  related: (id: number) => api.get<DestinationRelated>(`${base}/${id}/related`),
  create: (b: SaveDestination) => api.post<Destination>(base, b),
  update: (id: number, b: SaveDestination) => api.put<Destination>(`${base}/${id}`, b),
  setStatus: (ids: number[], status: 'Active' | 'Inactive') => api.post<BulkResult>(`${base}/status`, { ids, status }),
  deletePreview: (ids: number[]) => api.post<{ willDelete: NamedRef[]; willSkip: Skipped[] }>(`${base}/delete-preview`, { ids }),
  remove: (ids: number[]) => api.post<BulkResult>(`${base}/delete`, { ids }),
  mergePreview: (first: number, second: number) => api.get<{ first: MergeSide; second: MergeSide }>(`${base}/merge-preview`, { first, second }),
  merge: (keepId: number, removeId: number) => api.post<Destination>(`${base}/merge`, { keepId, removeId }),
  loadFromLibrary: () => api.post<{ destinations: number; attractions: number; message: string }>(`${base}/load-from-library`),
  sample: () => download(`${base}/sample`, undefined, 'destinations_sample.xlsx'),
  checkImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ImportCheck>(`${base}/import/check`, form);
  },
  importRows: (fileName: string, rows: ImportRow[], skipErrorRows: boolean) =>
    api.post<{ batchId: number; imported: number; skipped: number; message: string }>(`${base}/import`, { fileName, rows, skipErrorRows }),
  undoImport: (batchId: number) => api.post<string>(`${base}/import/${batchId}/undo`),
  exportExcel: (body: { ids?: number[] } & ListParams) => download(`${base}/export`, body, 'destinations.xlsx'),
  /** operating = only the countries and states the company operates in (destinations); off for addresses. */
  geo: (country?: string, operating = false) => api.get<GeoOptions>(`${base}/geo`, { country, operating: operating || undefined }),
  operatingRegions: () => api.get<OperatingRegions>(`${base}/operating-regions`),
  saveOperatingRegions: (b: { stateCodes: string[]; countryCodes: string[] }) => api.put<OperatingRegions>(`${base}/operating-regions`, b),
  mapSearch: (q: string) => api.get<MapPlace[]>(`${base}/map-search`, { q }),
  fetchAi: (b: { kind: 'destination' | 'attraction' | 'activity' | 'subtour'; name: string; address?: string | null; latitude?: number | null; longitude?: number | null; destination?: string | null; fields: string[] }) =>
    api.post<AiPlaceFetch>(`${base}/fetch-ai`, b),
  lookups: (kind: LookupKind) => api.get<PlaceLookup[]>(`${base}/lookups/${kind}`),
  addLookup: (kind: LookupKind, b: { name: string; description?: string | null; seats?: number | null; largeBags?: number | null }) =>
    api.post<PlaceLookup>(`${base}/lookups/${kind}`, { ...b, kind }),
};
